import React, {useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {isNameEmpty} from 'procedures/design/validation/validateProcedure';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Modal from 'ui-library/modals/Modal';

export interface DuplicateProcedureModalProps {
  sourceProcedureName: string;
  sourceProcedureTemplateCollectionId: string;
  sourceProcedureType: ProcedureType;
  onRequestClose: () => void;
  onRequestSubmit: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
}

export default function DuplicateProcedureModal(props: DuplicateProcedureModalProps): JSX.Element {
  const [name, setName] = useState(props.sourceProcedureName);
  const [templateCollectionId, setTemplateCollectionId] = useState<string>(
    props.sourceProcedureTemplateCollectionId
  );

  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.sourceProcedureType);
  const trimmedName = name.trim();
  const isNameBlank = isNameEmpty(trimmedName);
  const isNameInvalid =
    trimmedName === props.sourceProcedureName &&
    templateCollectionId === props.sourceProcedureTemplateCollectionId;

  function isSubmitDisabled(): boolean {
    return isNameInvalid || isNameBlank;
  }

  return (
    <Modal
      title={`Duplicate ${procedureString.toLowerCase()}`}
      buttons={[
        {
          text: 'Duplicate',
          submit: true,
          disabled: isSubmitDisabled(),
          tooltipTitle: isNameBlank
            ? `${procedureString} name must be non-empty.`
            : isNameInvalid
              ? `${procedureString} name ${trimmedName} is already in use in this template collection. Use a different name or select a different template collection.`
              : null,
        },
      ]}
      onRequestSubmit={async () => {
        await props.onRequestSubmit({name, templateCollectionId, procedureType: props.sourceProcedureType});
      }}
      onRequestClose={() => {
        props.onRequestClose();
      }}
      dataTestAttrs={{component: 'DuplicateProcedureModal', element: 'root'}}
    >
      <FormGroup>
        <Row>
          <Col md={6}>
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              autoFocus={true}
              {...getTestProps({component: 'DuplicateProcedureModal', element: 'nameInput'})}
            />
          </Col>
          <Col md={6}>
            <Label>Template collection</Label>
            <TemplateCollectionSelector
              value={templateCollectionId}
              onChange={(collection) => {
                setTemplateCollectionId(collection.id);
              }}
              includeOnlyOrgOwnedTemplateCollections={true}
            />
          </Col>
        </Row>
      </FormGroup>
    </Modal>
  );
}
